import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';
import * as wjcGridXlsx from '@mescius/wijmo.grid.xlsx';
import * as wjcOlap from '@mescius/wijmo.olap';
import {Component, OnInit, ViewChild} from '@angular/core';
import {ToasterService} from 'angular2-toaster';
import {DataService, OrientDBCollectionView} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {KobeacService} from './kobeac.service';
import {savePivotXlsx} from './pivot-xlsx';
import {TranslocoService} from '@jsverse/transloco';

/** 分析ブックマーク (AP_Bookmark, category='kobeac.analysis')。content は JSON 文字列。 */
interface AnalysisBookmark {
    '@rid'?: string;
    menu: string;
    name: string;
    uuid: string;
    sort?: number;
    content: string;
}

/**
 * kobe-ac 分析 (旧 /report/analyze、Pivot + 元データグリッド)。
 * ira-groupnet の analysis と同様に、集計設定 (PivotEngine.viewDefinition + オークション期間) を
 * AP_Bookmark にブックマーク保存し、メニュー (グループ) → ブックマークの順に選んで復元できる。
 */
@Component({
    standalone: false,
    selector: 'kobeac-analyze',
    templateUrl: 'analyze.component.html',
})
export class KobeacAnalyzeComponent implements OnInit {
    @ViewChild('dataGrid') dataGrid: wjcGrid.FlexGrid;
    @ViewChild('pivotGrid') pivotGrid: wjcOlap.PivotGrid;

    cv: OrientDBCollectionView;
    engine: wjcOlap.PivotEngine;
    /**
     * ピボット集計用の明細 (期間内の全件)。PivotEngine は CollectionView の表示中ページ (items) しか
     * 集計しないため、ページ分けされる元データ一覧 (cv) とは別のページ無し CollectionView を渡す。
     */
    pivotItems = new wjcCore.CollectionView([]);
    categoryMap: wjcGrid.DataMap;

    /** オークション日付範囲での絞り込み (Wijmo datepicker)。既定は 3 ヵ月前の月初〜当月末。 */
    range: { from: Date | null; to: Date | null } = KobeacAnalyzeComponent.defaultRange();

    private static readonly BOOKMARK_CATEGORY = 'kobeac.analysis';
    private static readonly NO_MENU = '(未分類)';
    /** 保存済みブックマーク (sort, name 順)。 */
    bookmarks: AnalysisBookmark[] = [];
    /** メニュー名 → ブックマーク。 */
    bookmarksByMenu: { [menu: string]: AnalysisBookmark[] } = {};
    menus: string[] = [];
    selectedMenu = '';
    selectedBookmark: AnalysisBookmark | null = null;
    /** 保存フォーム */
    bookmarkMenu = '';
    bookmarkName = '';
    savingBookmark = false;

    /** テンプレートで「(未分類)」メニューを判定して訳すために公開する (値は内部キーのまま)。 */
    readonly noMenu = KobeacAnalyzeComponent.NO_MENU;

    constructor(private dataService: DataService, private kobeac: KobeacService, private toaster: ToasterService,
                private transloco: TranslocoService) {
        this.cv = new OrientDBCollectionView(dataService, ActivePromotionConfig.DATABASE,
            "SELECT *, eval('`単価` * `個数`') as `金額` FROM AC_Item", 'AC_Item', '', 0, 5000, true, false, false);
        this.cv.url = ActivePromotionConfig.SERVER_URL;
        this.cv.tokenName = ActivePromotionConfig.DATABASE + '_token';
        this.cv.fetchPlan = '*:1';
        this.cv.itemsSourceFunction = (rows: any[]) => {
            this.kobeac.resolveCustomers(rows);
            for (const item of rows) {
                if (wjcCore.isObject(item['オークション']) && item['オークション']['日付']) {
                    item['オークション']['日付'] = new Date(String(item['オークション']['日付']).replace(' ', 'T'));
                }
                // eval() の結果が文字列で返ることがあるため、合計できるよう数値化する
                item['個数'] = Number(item['個数']) || 0;
                item['金額'] = Number(item['金額']) || 0;
            }
            // ピボットは期間内の全件で集計する
            this.pivotItems.sourceCollection = rows;
            return rows;
        };
        // filterOnServer=false で生成して問合せを保留し、既定期間の条件を設定してから
        // true にして 1 回だけ取得する (期間なしの全件取得を避ける)
        this.cv.filterSpecial = this.rangeFilter();
        this.cv.filterOnServer = true;
        // PivotEngine を TS で構築 (summary-management と同方式)。旧 analyze の
        // init() が push していた fields / rowFields / valueFields を宣言的に定義する。
        // wj-pivot-panel / wj-pivot-grid の双方へ同じ engine を [engine]/[itemsSource] で束縛する。
        this.engine = new wjcOlap.PivotEngine({
            autoGenerateFields: false,
            itemsSource: this.pivotItems,
            showRowTotals: wjcOlap.ShowTotals.Subtotals,
            showColumnTotals: wjcOlap.ShowTotals.Subtotals,
            showZeros: true,
            fields: [
                {binding: 'オークション.日付', header: 'オークション', dataType: wjcCore.DataType.Date, aggregate: wjcCore.Aggregate.Cnt},
                {binding: '売主.名称', header: '売主名', aggregate: wjcCore.Aggregate.Cnt},
                {binding: '個数', header: '個数', dataType: wjcCore.DataType.Number, aggregate: wjcCore.Aggregate.Sum},
                {binding: '金額', header: '金額', dataType: wjcCore.DataType.Number, aggregate: wjcCore.Aggregate.Sum},
                {binding: 'カテゴリ.大分類', header: '大分類'},
                {binding: 'カテゴリ.中分類', header: '中分類'},
                {binding: 'カテゴリ.小分類', header: '小分類'},
                {binding: 'カテゴリ.名称', header: 'カテゴリ名'},
                {binding: '買主.名称', header: '買主名', aggregate: wjcCore.Aggregate.Cnt},
            ],
            rowFields: ['オークション'],
            valueFields: ['個数', '金額'],
        });
    }

    async ngOnInit() {
        await this.kobeac.ensureLoaded();
        this.categoryMap = this.kobeac.buildDataMap(this.kobeac.categories, (c) => c['名称']);
        this.applyCategorySort();
        this.loadBookmarks();
    }

    // ---- ブックマーク -----------------------------------------------------

    loadBookmarks(): void {
        const sql = "SELECT FROM AP_Bookmark WHERE category = '" + KobeacAnalyzeComponent.BOOKMARK_CATEGORY + "' ORDER BY sort, name";
        this.dataService.query({sql: sql, count: sql, fetchPlan: '*:0'}, ActivePromotionConfig.SERVER_URL).subscribe({
            next: (res: any) => {
                const list: AnalysisBookmark[] = wjcCore.isArray(res.results) ? res.results : [];
                const byMenu: { [menu: string]: AnalysisBookmark[] } = {};
                const menus: string[] = [];
                list.forEach((b) => {
                    const m = this.menuOf(b);
                    if (!byMenu[m]) {
                        byMenu[m] = [];
                        menus.push(m);
                    }
                    byMenu[m].push(b);
                });
                this.bookmarks = list;
                this.bookmarksByMenu = byMenu;
                this.menus = menus;
                if (!this.selectedMenu || !byMenu[this.selectedMenu]) {
                    this.selectedMenu = menus.length ? menus[0] : '';
                }
                const sel = this.selectedBookmark;
                if (sel) {
                    this.selectedBookmark = list.filter((b) => b.uuid === sel.uuid)[0] || null;
                }
            },
            error: () => this.toaster.pop('error', this.transloco.translate('kobeac.analysis'), this.transloco.translate('kobeac.bookmark_load_failed')),
        });
    }

    private menuOf(b: AnalysisBookmark): string {
        return b.menu && String(b.menu).trim() ? String(b.menu).trim() : KobeacAnalyzeComponent.NO_MENU;
    }

    selectMenu(menu: string): void {
        this.selectedMenu = menu;
    }

    /** ブックマークを選択して集計設定 (期間 + ピボット定義) を復元する。 */
    selectBookmark(b: AnalysisBookmark): void {
        let content: any = {};
        try {
            content = JSON.parse(b.content || '{}');
        } catch (e) {
            this.toaster.pop('error', this.transloco.translate('kobeac.analysis'), this.transloco.translate('kobeac.bookmark_parse_failed'));
            return;
        }
        this.selectedBookmark = b;
        this.bookmarkName = b.name;
        this.bookmarkMenu = b.menu || '';
        this.range = {from: this.parseDate(content.from), to: this.parseDate(content.to)};
        this.applyRange();
        if (content.pivot) {
            this.engine.viewDefinition = content.pivot;
        }
    }

    /** 選択中ブックマークと名前・メニューが同じなら上書き (true)、違えば新規保存。 */
    get isOverwrite(): boolean {
        const s = this.selectedBookmark;
        return !!s && !!s['@rid'] && s.name === (this.bookmarkName || '').trim() && (s.menu || '') === (this.bookmarkMenu || '').trim();
    }

    saveBookmark(): void {
        const name = (this.bookmarkName || '').trim();
        if (!name) {
            this.toaster.pop('warning', this.transloco.translate('kobeac.analysis'), this.transloco.translate('kobeac.enter_bookmark_name'));
            return;
        }
        const menu = (this.bookmarkMenu || '').trim();
        const content = JSON.stringify({
            pivot: this.engine.viewDefinition,
            from: wjcCore.isDate(this.range.from) ? wjcCore.Globalize.formatDate(this.range.from as Date, 'yyyy-MM-dd') : null,
            to: wjcCore.isDate(this.range.to) ? wjcCore.Globalize.formatDate(this.range.to as Date, 'yyyy-MM-dd') : null,
        });
        const overwrite = this.isOverwrite;
        const uuid = overwrite ? this.selectedBookmark!.uuid : this.newUuid();
        const rec: any = overwrite
            ? {'@class': 'AP_Bookmark', '@rid': this.selectedBookmark!['@rid'], content: content}
            : {
                '@class': 'AP_Bookmark', category: KobeacAnalyzeComponent.BOOKMARK_CATEGORY, menu: menu, name: name,
                uuid: uuid, sort: this.bookmarks.length + 1, content: content,
            };
        this.savingBookmark = true;
        this.dataService.update([rec], ActivePromotionConfig.SERVER_URL).subscribe({
            next: () => {
                this.savingBookmark = false;
                this.toaster.pop('info', this.transloco.translate('kobeac.analysis'), this.transloco.translate(overwrite ? 'kobeac.overwritten_name' : 'kobeac.saved_name', {name: name}));
                this.selectedBookmark = {menu: menu, name: name, uuid: uuid, content: content};
                this.selectedMenu = menu || KobeacAnalyzeComponent.NO_MENU;
                this.loadBookmarks();
            },
            error: () => {
                this.savingBookmark = false;
                this.toaster.pop('error', this.transloco.translate('kobeac.analysis'), this.transloco.translate('kobeac.bookmark_save_failed'));
            },
        });
    }

    deleteBookmark(): void {
        const b = this.selectedBookmark;
        if (!b || !b['@rid']) {
            return;
        }
        if (!window.confirm(this.transloco.translate('kobeac.confirm_delete_bookmark', {name: b.name}))) {
            return;
        }
        this.dataService.command({sql: 'DELETE FROM AP_Bookmark WHERE @rid = ' + b['@rid']}, ActivePromotionConfig.SERVER_URL).subscribe({
            next: () => {
                this.toaster.pop('info', this.transloco.translate('kobeac.analysis'), this.transloco.translate('kobeac.deleted_name', {name: b.name}));
                this.newBookmark();
                this.loadBookmarks();
            },
            error: () => this.toaster.pop('error', this.transloco.translate('kobeac.analysis'), this.transloco.translate('kobeac.bookmark_delete_failed')),
        });
    }

    /** 新規入力に切り替える (選択解除)。 */
    newBookmark(): void {
        this.selectedBookmark = null;
        this.bookmarkName = '';
    }

    private parseDate(s: any): Date | null {
        if (!s) return null;
        const d = new Date(String(s) + 'T00:00:00');
        return isNaN(d.getTime()) ? null : d;
    }

    private newUuid(): string {
        return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    }

    /**
     * カテゴリのピボット並びを AC_Category.ソート 順にする。
     * カテゴリの各 PivotField に sortComparer を設定し、ソート値で比較する (表示ラベルはそのまま)。
     * ソート値が無い・同じ場合は null を返して既定の比較に委ねる。
     * 大/中/小分類は同名が複数カテゴリにまたがるため、最小ソート値を代表値にする。
     *
     * 以前は _PivotKey.compareTo をラップしていたが、手前のフィールドが日付 (オークション) だと
     * Date 同士を == で比べて「違う値」と判定し、カテゴリまで比較が進まず並びが効かなかった。
     */
    private applyCategorySort(): void {
        const levels = ['名称', '大分類', '中分類', '小分類'];
        const catSort: { [level: string]: { [key: string]: number } } = {名称: {}, 大分類: {}, 中分類: {}, 小分類: {}};
        for (const c of this.kobeac.categories) {
            const s = (typeof c['ソート'] === 'number') ? c['ソート'] : Number.MAX_SAFE_INTEGER;
            for (const level of levels) {
                const key = c[level];
                if (key == null || key === '') continue;
                if (!(key in catSort[level]) || s < catSort[level][key]) catSort[level][key] = s;
            }
        }
        this.categorySort = catSort;
        this.setCategoryComparers();
        // ブックマーク読込などでフィールドが作り直されても比較関数を付け直す
        this.engine.viewDefinitionChanged.addHandler(() => this.setCategoryComparers());
        try {
            this.engine.refresh(true);
        } catch (e) { /* noop */ }
    }

    /** カテゴリ階層ごとの「名前 → ソート値」 */
    private categorySort: { [level: string]: { [key: string]: number } } = {};

    /** 宣言済みの各カテゴリ PivotField に比較関数を設定する (header で対応付け) */
    private setCategoryComparers(): void {
        const catSort = this.categorySort;
        const byHeader: { [h: string]: string } = {'大分類': '大分類', '中分類': '中分類', '小分類': '小分類', 'カテゴリ名': '名称'};
        const fields = this.engine.fields;
        for (let i = 0; i < fields.length; i++) {
            const fld = fields[i];
            const level = byHeader[fld.header];
            if (!level) continue;
            const sortMap = catSort[level];
            fld.sortComparer = (a: any, b: any) => {
                const x = sortMap[a], y = sortMap[b];
                return (x != null && y != null && x !== y) ? (x < y ? -1 : 1) : null;
            };
        }
    }

    /** 既定の期間: 開始日 = 3 ヵ月前の月初、終了日 = 当月末。 */
    static defaultRange(): { from: Date; to: Date } {
        const now = new Date();
        return {
            from: new Date(now.getFullYear(), now.getMonth() - 3, 1),
            to: new Date(now.getFullYear(), now.getMonth() + 1, 0),
        };
    }

    applyRange(): void {
        this.cv.filterSpecial = this.rangeFilter();
    }

    /** 期間の絞り込み条件 (AC_Item.filterSpecial 用)。期間未指定なら空文字。 */
    private rangeFilter(): string {
        const conds: string[] = [];
        if (wjcCore.isDate(this.range.from)) {
            conds.push("`日付` >= date('" + wjcCore.Globalize.formatDate(this.range.from as Date, 'yyyy-MM-dd') + "','yyyy-MM-dd')");
        }
        if (wjcCore.isDate(this.range.to)) {
            // 終了日は当日を含めるため翌日未満で比較する
            const to = new Date((this.range.to as Date).getTime() + 24 * 60 * 60 * 1000);
            conds.push("`日付` < date('" + wjcCore.Globalize.formatDate(to, 'yyyy-MM-dd') + "','yyyy-MM-dd')");
        }
        // link 先 `日付` の traversal はフルスキャンになるため、AC_Auction の副問合せで絞る
        return conds.length
            ? "`オークション` IN (SELECT FROM AC_Auction WHERE " + conds.join(' AND ') + ')'
            : '';
    }

    resetRange(): void {
        this.range = {from: null, to: null};
        this.cv.filterSpecial = '';
    }

    export(pivot?: boolean): void {
        const filename = 'export(' + wjcCore.Globalize.formatDate(new Date(), 'yyyyMMddHHmmss') + ').xlsx';
        if (pivot) {
            // ピボット表は表示どおり (行見出し=行フィールド、列見出し=列フィールド×値) に出力する
            if (this.pivotGrid != null) {
                savePivotXlsx(this.pivotGrid, filename);
            }
        } else if (this.dataGrid != null) {
            wjcGridXlsx.FlexGridXlsxConverter.saveAsync(this.dataGrid, {includeColumnHeaders: true, includeCellStyles: false}, filename);
        }
    }
}
